import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { GuestCatalogLayout } from "@/components/guest-catalog/GuestCatalogLayout";
import { useGuestOpacTenantSlug } from "@/hooks/useGuestOpacTenantSlug";
import { useGuestOpacBranding } from "@/hooks/useGuestOpacBranding";
import { contactApi } from "@/lib/api/contact";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GuestCatalogSectionCard } from "@/components/guest-catalog/GuestCatalogSectionCard";

export function GuestContactPage() {
  const tenantSlug = useGuestOpacTenantSlug();
  const branding = useGuestOpacBranding(tenantSlug);
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");
    try {
      await contactApi.submit(formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error: unknown) {
      setSubmitStatus("error");
      const err = error as { response?: { data?: { message?: string } } };
      setErrorMessage(err.response?.data?.message || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GuestCatalogLayout libraryName={branding.libraryName} logoUrl={branding.logoUrl}>
      <Button variant="ghost" size="sm" className="mb-4 -ml-2" asChild>
        <Link to="/opac">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to catalog
        </Link>
      </Button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contact the library</h1>
        <p className="mt-1 text-sm text-gray-500">
          Send a message to {branding.libraryName}. We typically respond within one business day.
        </p>
      </div>

      <GuestCatalogSectionCard>
        {submitStatus === "success" && (
          <Alert className="mb-6 border-emerald-600/30 bg-emerald-50">
            <CheckCircle2 className="h-4 w-4 text-emerald-700" />
            <AlertDescription className="text-emerald-800">
              Your message was sent. The library will get back to you soon.
            </AlertDescription>
          </Alert>
        )}
        {submitStatus === "error" && (
          <Alert className="mb-6 border-red-300 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">{errorMessage}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="guest-contact-name">Full name</Label>
              <Input
                id="guest-contact-name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="guest-contact-email">Email</Label>
              <Input
                id="guest-contact-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="guest-contact-subject">Subject</Label>
            <Input
              id="guest-contact-subject"
              required
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="guest-contact-message">Message</Label>
            <Textarea
              id="guest-contact-message"
              required
              rows={5}
              className="resize-none"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            />
          </div>
          <Button type="submit" disabled={isSubmitting} className="gap-2">
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Send message
          </Button>
        </form>
      </GuestCatalogSectionCard>
    </GuestCatalogLayout>
  );
}
